import { useState, useEffect, useCallback, useRef } from 'react';
import { messengerService } from '../services/messengerService';
import type { ChatMessage } from '../types/messenger.types';

const PAGE_SIZE = 50;

interface UseConversationResult {
  messages: ChatMessage[];
  loading: boolean;
  loadingOlder: boolean;
  hasMore: boolean;
  sending: boolean;
  statusMessage: string | null;
  conversationId: number | null;
  sendMessage: (text: string) => void;
  loadOlder: () => void;
  reset: () => void;
}

export function useConversation(agentId: number): UseConversationResult {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [sending, setSending] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<number | null>(null);

  // convIds triés asc — on les garde pour savoir où paginer
  const convIdsRef = useRef<number[]>([]);
  // Curseur de pagination : { convIndex, oldestMsgId }
  const cursorRef = useRef<{ convIndex: number; oldestMsgId: number | null }>({ convIndex: 0, oldestMsgId: null });
  const abortRef = useRef<(() => void) | null>(null);

  // Charge les N messages les plus récents au départ
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setMessages([]);
    setHasMore(false);
    setConversationId(null);
    convIdsRef.current = [];
    cursorRef.current = { convIndex: 0, oldestMsgId: null };

    (async () => {
      try {
        const convIds = await messengerService.fetchAllConversationIds(agentId);
        if (cancelled || convIds.length === 0) return;

        convIdsRef.current = convIds;
        const lastConvId = convIds[convIds.length - 1];
        setConversationId(lastConvId);

        // Charger les PAGE_SIZE derniers messages de la conversation la plus récente
        const { messages: msgs, hasMore: more } = await messengerService.fetchMessages(
          lastConvId,
          { limit: PAGE_SIZE }
        );
        if (cancelled) return;

        // S'il n'y a pas assez de messages dans la dernière conv, on peut avoir plus dans les précédentes
        const hasPreviousConvs = convIds.length > 1;
        setHasMore(more || hasPreviousConvs);

        // Curseur : pointe sur le message le plus ancien chargé dans cette conv
        cursorRef.current = {
          convIndex: convIds.length - 1,
          oldestMsgId: msgs.length > 0 ? Number(msgs[0].id) : null,
        };

        setMessages(msgs);
      } catch {
        /* start fresh */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [agentId]);

  // Charge les messages plus anciens (déclenchée par l'IntersectionObserver en haut)
  const loadOlder = useCallback(async () => {
    if (loadingOlder || !hasMore) return;
    setLoadingOlder(true);

    try {
      const { convIndex, oldestMsgId } = cursorRef.current;
      const convIds = convIdsRef.current;
      let collected: ChatMessage[] = [];
      let newConvIndex = convIndex;
      let newOldestMsgId = oldestMsgId;
      let newHasMore = false;

      // Étape 1 : paginer dans la conv courante avec before_id
      if (oldestMsgId !== null) {
        const { messages: older, hasMore: more } = await messengerService.fetchMessages(
          convIds[convIndex],
          { limit: PAGE_SIZE, beforeId: oldestMsgId }
        );
        if (older.length > 0) {
          collected = [...older];
          newOldestMsgId = Number(older[0].id);
        } else {
          // Rien avant ce curseur dans cette conv → passer à la précédente
          newOldestMsgId = null;
        }
        newHasMore = more;
      }

      // Étape 2 : si la conv courante est épuisée (0 résultats ou plus de curseur valide),
      // remonter dans les convs précédentes jusqu'à avoir PAGE_SIZE messages
      if (!newHasMore && collected.length < PAGE_SIZE) {
        while (newConvIndex > 0) {
          newConvIndex--;
          newOldestMsgId = null;

          const { messages: prevMsgs, hasMore: prevMore } = await messengerService.fetchMessages(
            convIds[newConvIndex],
            { limit: PAGE_SIZE }
          );

          if (prevMsgs.length > 0) {
            collected = [...prevMsgs, ...collected];
            newOldestMsgId = Number(prevMsgs[0].id);
          }

          newHasMore = prevMore;

          // Stopper si cette conv a plus de messages (on reviendra dessus au prochain loadOlder)
          // ou si on a déjà assez de messages
          if (prevMore || collected.length >= PAGE_SIZE) break;
        }
      }

      cursorRef.current = { convIndex: newConvIndex, oldestMsgId: newOldestMsgId };
      setHasMore(newHasMore || newConvIndex > 0);

      if (collected.length > 0) {
        setMessages(prev => [...collected, ...prev]);
      }
    } catch {
      /* silencieux */
    } finally {
      setLoadingOlder(false);
    }
  }, [loadingOlder, hasMore]);

  const sendMessage = useCallback((text: string) => {
    if (!text.trim() || sending) return;

    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      content: text.trim(),
      timestamp: new Date().toISOString(),
      isFromAgent: false,
    };

    setMessages(prev => [...prev, userMsg]);
    setSending(true);
    setStatusMessage(null);

    const assistantId = `agent-${Date.now()}`;
    let assistantAdded = false;

    abortRef.current = messengerService.streamMessage(
      agentId,
      text.trim(),
      conversationId,
      {
        onToken: (token) => {
          if (!assistantAdded) {
            assistantAdded = true;
            setMessages(prev => [...prev, {
              id: assistantId,
              content: token,
              timestamp: new Date().toISOString(),
              isFromAgent: true,
              streaming: true,
            }]);
          } else {
            setMessages(prev => prev.map(m =>
              m.id === assistantId ? { ...m, content: m.content + token } : m
            ));
          }
          setStatusMessage(null);
        },
        onDone: (newConvId) => {
          setMessages(prev => prev.map(m =>
            m.id === assistantId ? { ...m, streaming: false } : m
          ));
          if (newConvId) {
            setConversationId(newConvId);
            // Si c'est une nouvelle conv, l'ajouter à la liste
            if (!convIdsRef.current.includes(newConvId)) {
              convIdsRef.current = [...convIdsRef.current, newConvId];
              cursorRef.current = { convIndex: convIdsRef.current.length - 1, oldestMsgId: null };
            }
          }
          setSending(false);
          setStatusMessage(null);
        },
        onError: (err) => {
          setMessages(prev => [...prev, {
            id: `error-${Date.now()}`,
            content: `Erreur : ${err}`,
            timestamp: new Date().toISOString(),
            isFromAgent: true,
            error: true,
          }]);
          setSending(false);
          setStatusMessage(null);
        },
        onStatus: (msg) => setStatusMessage(msg),
      }
    );
  }, [agentId, conversationId, sending]);

  const reset = useCallback(() => {
    abortRef.current?.();
    setMessages([]);
    setConversationId(null);
    setSending(false);
    setStatusMessage(null);
    setHasMore(false);
    convIdsRef.current = [];
    cursorRef.current = { convIndex: 0, oldestMsgId: null };
  }, []);

  useEffect(() => () => { abortRef.current?.(); }, []);

  return { messages, loading, loadingOlder, hasMore, sending, statusMessage, conversationId, sendMessage, loadOlder, reset };
}
